import React, { useEffect, useState, useRef } from "react";
import { ImCross } from "react-icons/im";
import { toast } from "react-toastify";
import { IoCopyOutline } from "react-icons/io5";
import "react-toastify/dist/ReactToastify.css";
import "antd/dist/reset.css";
import dayjs from "dayjs";
import Switch from "react-switch";
import { GoogleMap, LoadScript, Marker, Circle } from "@react-google-maps/api";
import axios from "axios";
import "./popup.css";
import Autocomplete from "react-google-autocomplete";

const API_BASE_URL = "https://backend.washta.com";
const DAYS = [
  "monday", "tuesday", "wednesday", "thursday",
  "friday", "saturday", "sunday",
];

const DAY_LABELS = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};
const FIXED_RADIUS = 5;

const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const val = `${hh}:${mm}`;
      const ampm = h < 12 ? "am" : "pm";
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const label = `${h12}:${mm}${ampm}`;
      options.push({ val, label });
    }
  }
  return options;
};
const TIME_OPTIONS = generateTimeOptions();

const getEmptyFormData = (ownerId) => ({
  Owner: ownerId,
  city: "",
  country: "UAE",
  isActive: true,
  radius: FIXED_RADIUS,
  markerPosition: { lat: 25.354826, lng: 55.381007 },
  parkingPositions: [],
  location: {
    country_city: "UAE , Dubai",
    display_name: "",
    text: "",
    lat: 24.85,
    long: 67.05,
  },
  estimatedServiceTime: "",
  timing: {
    monday: { open: false, from: "", to: "" },
    tuesday: { open: false, from: "", to: "" },
    wednesday: { open: false, from: "", to: "" },
    thursday: { open: false, from: "", to: "" },
    friday: { open: false, from: "", to: "" },
    saturday: { open: false, from: "", to: "" },
    sunday: { open: false, from: "", to: "" },
  },
});

const getEditLocationData = (editData) => {
  const locationEntry = Array.isArray(editData?.locations) && editData.locations.length > 0
    ? editData.locations[0]
    : editData?.location || {};

  const locationPoint = locationEntry?.location || editData?.location || {};
  const coordinates = Array.isArray(locationPoint?.coordinates)
    ? locationPoint.coordinates
    : [];

  const displayName =
    locationEntry?.display_name ||
    locationEntry?.displayName ||
    locationEntry?.location?.display_name ||
    locationEntry?.location?.displayName ||
    editData?.display_name ||
    editData?.displayName ||
    locationPoint?.text ||
    locationEntry?.location?.text ||
    editData?.shopName ||
    "";

  return {
    locationEntry,
    locationPoint,
    displayName,
    text: locationPoint?.text || "",
    lat: coordinates[1],
    long: coordinates[0],
  };
};

const TimeSelect = ({ value, onChange, disabled }) => (
  <select
    className="time-select-dropdown"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
  >
    <option value="" disabled>--:--</option>
    {TIME_OPTIONS.map((t) => (
      <option key={t.val} value={t.val}>{t.label}</option>
    ))}
  </select>
);

const StepIndicator = ({ step }) => (
  <div className="step-indicator">
    {[1, 2].map((s, i) => (
      <React.Fragment key={s}>
        <div className={`step-dot ${step === s ? "active" : step > s ? "done" : ""}`}>
          {step > s ? "✓" : s}
        </div>
        {i < 1 && <div className={`step-line ${step > s ? "done" : ""}`} />}
      </React.Fragment>
    ))}
  </div>
);

const PopupSellar = ({ Cross, id, fetchData, editData }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => getEmptyFormData(id));
  const [uploading, setUploading] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceDay, setCopySourceDay] = useState(null);
  const [selectedDaysForCopy, setSelectedDaysForCopy] = useState(new Set(DAYS));
  const token = localStorage.getItem("accessToken");
  const autocompleteRef = useRef(null);
  const lastSelectedSuggestionText = useRef("");

  useEffect(() => {
    const captureSuggestionText = (e) => {
      const pacItem = e.target.closest(".pac-item");
      if (pacItem) {
        lastSelectedSuggestionText.current = pacItem.textContent
          .replace(/\s+/g, " ")
          .trim();
      }
    };
    document.addEventListener("mousedown", captureSuggestionText, true);
    document.addEventListener("touchstart", captureSuggestionText, true);
    return () => {
      document.removeEventListener("mousedown", captureSuggestionText, true);
      document.removeEventListener("touchstart", captureSuggestionText, true);
    };
  }, []);

  const normalizeTimeValue = (value) => {
    if (!value && value !== 0) return "";
    const raw = String(value).trim();
    if (!raw) return "";
    const timeMatch = raw.match(/^(\d{1,2}):(\d{2})\s*([aApP][mM])?$/);
    if (timeMatch) {
      let hours = Number(timeMatch[1]);
      const minutes = Number(timeMatch[2]);
      const meridiem = timeMatch[3]?.toLowerCase();

      if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) return "";

      if (meridiem) {
        if (hours < 1 || hours > 12) return "";
        if (meridiem === "pm" && hours !== 12) hours += 12;
        if (meridiem === "am" && hours === 12) hours = 0;
      } else if (hours < 0 || hours > 23) {
        return "";
      }

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }

    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  };

  const getIncomingDayTiming = (timing, day) => {
    if (!timing || typeof timing !== "object") return { open: false, from: "", to: "" };

    const candidates = [
      day,
      day.toLowerCase(),
      day.charAt(0).toUpperCase() + day.slice(1),
      day.slice(0, 3),
      day.slice(0, 3).toUpperCase(),
    ];

    const source = candidates
      .map((key) => timing[key])
      .find((entry) => entry && typeof entry === "object");

    return source || { open: false, from: "", to: "" };
  };

  const handleTimeChange = (day, type, value) => {
    setFormData((prev) => ({
      ...prev,
      timing: {
        ...prev.timing,
        [day]: { ...prev.timing[day], [type]: value, open: true },
      },
    }));
  };

  useEffect(() => {
    if (editData) {
      const locationData = getEditLocationData(editData);
      const updatedTiming = {};
      DAYS.forEach((day) => {
        const source = getIncomingDayTiming(editData?.timing, day);
        const normalizedFrom = normalizeTimeValue(source.from);
        const normalizedTo = normalizeTimeValue(source.to);
        updatedTiming[day] = {
          open: Boolean(source.open || normalizedFrom || normalizedTo),
          from: normalizedFrom,
          to: normalizedTo,
        };
      });
      setStep(1);
      setFormData({
        ...getEmptyFormData(editData.Owner || id),
        Owner: editData.Owner,
        city: editData.city || locationData.locationEntry?.city || "",
        country: editData.country || locationData.locationEntry?.country || "UAE",
        isActive: editData.isActive,
        radius: FIXED_RADIUS,
        markerPosition: {
          lat: locationData.lat || 25.354826,
          lng: locationData.long || 55.381007,
        },
        location: {
          country_city: "UAE , Dubai",
          display_name: locationData.displayName,
          text: locationData.text,
          lat: locationData.lat || 24.85,
          long: locationData.long || 67.05,
        },
        estimatedServiceTime: editData.estimatedServiceTime,
        timing: updatedTiming,
      });
      return;
    }
    setStep(1);
    setFormData(getEmptyFormData(id));
  }, [editData]);

  const handleActiveChange = (day) => {
    setFormData((prev) => ({
      ...prev,
      timing: {
        ...prev.timing,
        [day]: {
          ...prev.timing[day],
          open: !prev.timing[day].open,
          from: prev.timing[day].open ? "" : prev.timing[day].from,
          to: prev.timing[day].open ? "" : prev.timing[day].to,
        },
      },
    }));
  };
  const openCopyModal = (day) => {
    setCopySourceDay(day);
    const otherActiveDays = new Set(
      DAYS.filter((d) => d !== day && formData.timing[d].open)
    );
    setSelectedDaysForCopy(otherActiveDays);
    setShowCopyModal(true);
  };

  const handleDayToggleInCopyModal = (day) => {
    const newSelected = new Set(selectedDaysForCopy);
    if (newSelected.has(day)) newSelected.delete(day);
    else newSelected.add(day);
    setSelectedDaysForCopy(newSelected);
  };

  const handleSelectAllDaysForCopy = (selectAll) => {
    if (selectAll) {
      setSelectedDaysForCopy(new Set(DAYS.filter((d) => d !== copySourceDay)));
    } else {
      setSelectedDaysForCopy(new Set());
    }
  };

  const handleApplyCopyTiming = () => {
    if (!copySourceDay) return;
    const { from, to } = formData.timing[copySourceDay];
    const updatedTiming = { ...formData.timing };
    selectedDaysForCopy.forEach((day) => {
      updatedTiming[day] = { open: true, from, to };
    });
    setFormData((prev) => ({ ...prev, timing: updatedTiming }));
    setShowCopyModal(false);
    toast.success("Times copied to selected days!");
  };

  const stripCityCountrySuffix = (text, city, country) => {
    if (!text) return "";
    const segments = text.split(" - ").map((s) => s.trim()).filter(Boolean);
    if (segments.length <= 1) return text.trim();

    const cityLower = (city || "").toLowerCase();
    const countryLower = (country || "").toLowerCase();

    const filtered = segments.filter((seg) => {
      const segLower = seg.toLowerCase();
      if (cityLower && segLower === cityLower) return false;
      if (countryLower && segLower === countryLower) return false;
      return true;
    });

    return (filtered.length > 0 ? filtered : segments).join(" - ").trim();
  };

  const getShortDisplayName = (place, city, country, fullText) => {
    const source = fullText || place.name || place.formatted_address || "";
    return stripCityCountrySuffix(source, city, country) || place.name || "";
  };

  const handlePlaceSelected = (place) => {
    if (place && place.geometry) {
      const addressComponents = place.address_components;
      const city = addressComponents.find(
        (c) => c.types.includes("locality") || c.types.includes("administrative_area_level_1")
      )?.long_name || "";
      const country = addressComponents.find((c) => c.types.includes("country"))?.long_name || "";

      const selectedText =
        lastSelectedSuggestionText.current ||
        place.name ||
        place.formatted_address ||
        "";

      const shortDisplayName = getShortDisplayName(place, city, country, selectedText);

      setFormData((prev) => ({
        ...prev,
        city,
        country: country || prev.country,
        location: {
          country_city: `${country}, ${city}`,
          display_name: shortDisplayName,
          text: selectedText,
          full_address: place.formatted_address || "",
          lat: place.geometry.location.lat(),
          long: place.geometry.location.lng(),
        },
        markerPosition: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
        radius: FIXED_RADIUS,
      }));

      if (autocompleteRef.current) {
        autocompleteRef.current.value = selectedText;
      }

      lastSelectedSuggestionText.current = "";
    }
  };

  const generateParkingPositions = (center, radiusInKm) => {
    const radiusInMeters = radiusInKm * 1000;
    const positions = [];
    const numMarkers = Math.floor(Math.random() * 20) + 5;
    for (let i = 0; i < numMarkers; i++) {
      const angle = Math.random() * 2 * Math.PI;
      const distance = Math.random() * radiusInMeters;
      const offsetLat = (distance * Math.sin(angle)) / 111300;
      const offsetLng = (distance * Math.cos(angle)) / (111300 * Math.cos((center.lat * Math.PI) / 180));
      positions.push({ lat: center.lat + offsetLat, lng: center.lng + offsetLng });
    }
    return positions;
  };

  const handleRadiusChange = (e) => {
    return e;
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setFormData((prev) => ({
      ...prev,
      markerPosition: { lat, lng },
      location: { ...prev.location, lat, long: lng },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const preventWheel = (e) => e.preventDefault();
  const handleFocus = (e) => e.target.addEventListener("wheel", preventWheel, { passive: false });

  const validateStep1 = () => {
    const isAnyActive = DAYS.some((day) => formData.timing[day].open);
    if (!isAnyActive) { toast.error("Please activate at least one day"); return false; }
    for (const day of DAYS) {
      const { from, to, open } = formData.timing[day];
      if (open && (!from || !to)) {
        toast.error(`Please set start and end time for ${DAY_LABELS[day]}`);
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.location?.text) {
      toast.error("Please fill all location fields");
      return false;
    }
    if (!formData.location?.display_name?.trim()) {
      toast.error("Please enter a location display name");
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((s) => s + 1);
  };

  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    if (!validateStep2()) return;
    setUploading(true);
    try {
      const payload = {
        isActive: true,
        city: formData.city,
        country: formData.country || "UAE",
        location: {
          text: formData.location.text,
          display_name: formData.location.display_name,
          city: formData.city,
          country: formData.country || "UAE",
          _id: formData.location._id,
          lat: formData.location.lat,
          long: formData.location.long,
          coordinates: [formData.location.long, formData.location.lat],
        },
        timing: Object.keys(formData.timing).reduce((acc, day) => {
          acc[day] = { open: formData.timing[day].open, from: formData.timing[day].from, to: formData.timing[day].to };
          return acc;
        }, {}),
        radius: FIXED_RADIUS,
        estimatedServiceTime: formData.estimatedServiceTime,
      };
      const apiEndpoint = editData ? `/api/seller/shop/${editData._id}` : "/api/seller/shop";
      const httpMethod = editData ? "PATCH" : "POST";
      const response = await axios({
        method: httpMethod,
        url: `${API_BASE_URL}${apiEndpoint}`,
        data: payload,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (response.status >= 200 && response.status < 300) {
        toast.success(editData ? "Location updated successfully" : "New location saved");
        fetchData();
        Cross();
      } else {
        toast.error("Failed to submit location details");
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("This location already exists");
      } else if (error.response?.status === 429) {
        toast.error("Too many requests, please try again later.");
      } else {
        toast.error(error?.response?.data?.error || error?.response?.data?.message || "Error submitting location details");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="app">
      <div className="popup-background">
        <div className="popup">

          <div className="popup-header">
            <h2>
              {step === 1 ? "Working Hours" : "Location & Radius"}
            </h2>
            <ImCross onClick={Cross} className="close-icon" />
          </div>

          <StepIndicator step={step} />

          {step === 1 && (
            <>
              <div className="timing-section-header">
                <div className="timing-section-title">Set your working hours</div>
              </div>

              <div className="timing-list">
                {DAYS.map((day) => {
                  const isActive = formData.timing[day].open;
                  return (
                    <div key={day} className={`timing-row-new ${isActive ? "active-day" : ""}`}>
                      <Switch
                        onChange={() => handleActiveChange(day)}
                        checked={isActive}
                        onColor="#6470dd"
                        offColor="#ddd"
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        height={22}
                        width={42}
                        boxShadow="0px 1px 4px rgba(0,0,0,0.15)"
                        activeBoxShadow="0px 0px 0px 3px rgba(26,26,46,0.15)"
                      />

                      <span className={`day-label-new ${isActive ? "" : "muted"}`}>
                        {DAY_LABELS[day]}
                      </span>

                      {isActive ? (
                        <>
                          <div className="time-selects-wrap justify-center">
                            <TimeSelect
                              value={formData.timing[day].from}
                              onChange={(val) => handleTimeChange(day, "from", val)}
                            />
                            <span className="time-dash">–</span>
                            <TimeSelect
                              value={formData.timing[day].to}
                              onChange={(val) => handleTimeChange(day, "to", val)}
                            />
                          </div>

                          <div className="timing-actions">
                            <button
                              className="timing-icon-btn"
                              title="Copy to other days"
                              onClick={() => openCopyModal(day)}
                            >
                              <IoCopyOutline size={14} />
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="time-selects-placeholder" />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="row-popup">
                <button className="btn-left" onClick={Cross}>Cancel</button>
                <button className="btn-right" onClick={nextStep}>Continue →</button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <label className="label-popup-seller">Location <sup>*</sup></label>
              <LoadScript googleMapsApiKey="AIzaSyDkYZEMg8ZH5EhRqdkLF-GyeHrhpZBlSd0" libraries={["places"]}>
                <Autocomplete
                  ref={autocompleteRef}
                  onPlaceSelected={handlePlaceSelected}
                  options={{ types: ["establishment", "geocode"], componentRestrictions: { country: "AE" } }}
                  value={formData.location.text}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      location: { ...prev.location, text: e.target.value },
                    }))
                  }
                  placeholder="Search for an address in UAE"
                  style={{
                    width: "100%", height: "44px", padding: "0 14px", boxSizing: "border-box",
                    borderRadius: "10px", border: "1.5px solid #e8e8f0", marginBottom: "14px",
                    fontSize: "14px", color: "#1a1a2e", background: "#fafafa", outline: "none",
                  }}
                />
              </LoadScript>

              <div className="addtion-spantag" style={{ marginTop: 12 }}>
                <label className="label-popup-seller">Location display name <sup>*</sup></label>
                <input
                  type="text"
                  placeholder="e.g. Al Nahda Branch"
                  value={formData.location.display_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      location: { ...prev.location, display_name: e.target.value },
                    }))
                  }
                />
              </div>

              <label className="label-popup-seller">Service Radius <sup>*</sup></label>
              <div className="addtion-spantag">
                <input
                  type="number"
                  name="radius"
                  value={FIXED_RADIUS}
                  readOnly
                  disabled
                  onChange={handleRadiusChange}
                  style={{ paddingRight: "80px" }}
                />
                <span className="span-tag-popup">Kilometers</span>
              </div>

              <div style={{ marginTop: "14px" }}>
                <LoadScript googleMapsApiKey="AIzaSyDkYZEMg8ZH5EhRqdkLF-GyeHrhpZBlSd0" libraries={["places"]} async defer>
                  <div className="map-wrapper">
                    <GoogleMap
                      mapContainerStyle={{ height: "280px", width: "100%" }}
                      center={formData.markerPosition}
                      zoom={12}
                      onClick={handleMapClick}
                       options={{
    gestureHandling: "cooperative", 
    scrollwheel: false,
    disableDoubleClickZoom: false,
  }}
                    >
                      <Marker position={formData.markerPosition} />
                      {formData.radius && (
                        <Circle
                          center={formData.markerPosition}
                          radius={FIXED_RADIUS * 1000}
                          options={{
                            fillColor: "rgba(116, 126, 239, 0.15)",
                            strokeColor: "#6b5fe4",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      )}
                      {formData.parkingPositions.map((position, index) => (
                        <Marker
                          key={index}
                          position={position}
                          icon={{
                            url: "data:image/svg+xml;base64," + btoa(`
                              <svg width="69" height="69" viewBox="0 0 69 69" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2.5" y="2.5" width="64" height="64" rx="32" fill="#6b5fe4"/>
                                <rect x="2.5" y="2.5" width="64" height="64" rx="32" stroke="white" stroke-width="5"/>
                                <path d="M23.1716 49V21H35.2916C37.7982 21 39.9582 21.4133 41.7716 22.24C43.5849 23.04 44.9849 24.2 45.9716 25.72C46.9582 27.24 47.4516 29.0533 47.4516 31.16C47.4516 33.24 46.9582 35.04 45.9716 36.56C44.9849 38.08 43.5849 39.2533 41.7716 40.08C39.9582 40.88 37.7982 41.28 35.2916 41.28H26.7716L29.6516 38.36V49H23.1716ZM29.6516 39.08L26.7716 36H34.9316C36.9316 36 38.4249 35.5733 39.4116 34.72C40.3982 33.8667 40.8916 32.68 40.8916 31.16C40.8916 29.6133 40.3982 28.4133 39.4116 27.56C38.4249 26.7067 36.9316 26.28 34.9316 26.28H26.7716L29.6516 23.2V39.08Z" fill="white"/>
                              </svg>
                            `),
                            scaledSize: new window.google.maps.Size(30, 30),
                            anchor: new window.google.maps.Point(30, 20),
                          }}
                        />
                      ))}
                    </GoogleMap>
                  </div>
                </LoadScript>
              </div>

              <div className="row-popup">
                <button className="btn-left" onClick={prevStep}>← Back</button>
                <button className="btn-right" onClick={handleSubmit} disabled={uploading}>
                  {uploading ? "Saving..." : editData ? "Update Location" : "Create Location"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showCopyModal && (
        <div className="copy-modal-overlay">
          <div className="copy-modal-box">
            <div className="copy-modal-header">
              <h3>COPY TIMES TO</h3>
              <button type="button" className="copy-modal-close" onClick={() => setShowCopyModal(false)}>✕</button>
            </div>
            <div className="copy-modal-content">
              <label className="copy-modal-item full-width">
                <input
                  type="checkbox"
                  checked={selectedDaysForCopy.size === DAYS.filter(d => d !== copySourceDay).length}
                  onChange={(e) => handleSelectAllDaysForCopy(e.target.checked)}
                />
                <span>Select all</span>
              </label>
              {DAYS.filter((d) => d !== copySourceDay).map((day) => (
                <label key={day} className="copy-modal-item">
                  <input
                    type="checkbox"
                    checked={selectedDaysForCopy.has(day)}
                    onChange={() => handleDayToggleInCopyModal(day)}
                  />
                  <span>{DAY_LABELS[day]}</span>
                </label>
              ))}
            </div>
            <div className="copy-modal-footer">
              <button type="button" className="copy-modal-cancel" onClick={() => setShowCopyModal(false)}>Cancel</button>
              <button type="button" className="copy-modal-apply" onClick={handleApplyCopyTiming} style={{background:'#6b5fe4'}}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupSellar;
// import React, { useEffect, useState, useRef } from "react";
// import { ImCross } from "react-icons/im";
// import { toast } from "react-toastify";
// import { IoCopyOutline } from "react-icons/io5";
// import "react-toastify/dist/ReactToastify.css";
// import "antd/dist/reset.css";
// import dayjs from "dayjs";
// import Switch from "react-switch";
// import { GoogleMap, LoadScript, Marker, Circle } from "@react-google-maps/api";
// import axios from "axios";
// import "./popup.css";
// import Autocomplete from "react-google-autocomplete";

// const API_BASE_URL = "https://backend.washta.com";
// const DAYS = [
//   "monday", "tuesday", "wednesday", "thursday",
//   "friday", "saturday", "sunday",
// ];

// const DAY_LABELS = {
//   monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
//   thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
// };
// const FIXED_RADIUS = 5;

// // 24 Hours option: from 11:59am to 11:59pm (24hr format used internally)
// const FULL_DAY_FROM = "11:59";
// const FULL_DAY_TO = "23:59";

// const generateTimeOptions = () => {
//   const options = [];
//   for (let h = 0; h < 24; h++) {
//     for (let m = 0; m < 60; m += 15) {
//       const hh = String(h).padStart(2, "0");
//       const mm = String(m).padStart(2, "0");
//       const val = `${hh}:${mm}`;
//       const ampm = h < 12 ? "am" : "pm";
//       const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
//       const label = `${h12}:${mm}${ampm}`;
//       options.push({ val, label });
//     }
//   }
//   return options;
// };
// const TIME_OPTIONS = generateTimeOptions();

// const getEmptyFormData = (ownerId) => ({
//   Owner: ownerId,
//   city: "",
//   isActive: true,
//   radius: FIXED_RADIUS,
//   markerPosition: { lat: 25.354826, lng: 55.381007 },
//   parkingPositions: [],
//   location: {
//     country_city: "UAE , Dubai",
//     display_name: "",
//     text: "",
//     lat: 24.85,
//     long: 67.05,
//   },
//   estimatedServiceTime: "",
//   timing: {
//     monday: { open: false, from: "", to: "" },
//     tuesday: { open: false, from: "", to: "" },
//     wednesday: { open: false, from: "", to: "" },
//     thursday: { open: false, from: "", to: "" },
//     friday: { open: false, from: "", to: "" },
//     saturday: { open: false, from: "", to: "" },
//     sunday: { open: false, from: "", to: "" },
//   },
// });

// const getEditLocationData = (editData) => {
//   const locationEntry = Array.isArray(editData?.locations) && editData.locations.length > 0
//     ? editData.locations[0]
//     : editData?.location || {};

//   const locationPoint = locationEntry?.location || editData?.location || {};
//   const coordinates = Array.isArray(locationPoint?.coordinates)
//     ? locationPoint.coordinates
//     : [];

//   const displayName =
//     locationEntry?.display_name ||
//     locationEntry?.displayName ||
//     locationEntry?.location?.display_name ||
//     locationEntry?.location?.displayName ||
//     editData?.display_name ||
//     editData?.displayName ||
//     locationPoint?.text ||
//     locationEntry?.location?.text ||
//     editData?.shopName ||
//     "";

//   return {
//     locationEntry,
//     locationPoint,
//     displayName,
//     text: locationPoint?.text || "",
//     lat: coordinates[1],
//     long: coordinates[0],
//   };
// };

// const TimeSelect = ({ value, onChange, disabled }) => (
//   <select
//     className="time-select-dropdown"
//     value={value || ""}
//     onChange={(e) => onChange(e.target.value)}
//     disabled={disabled}
//   >
//     <option value="" disabled>--:--</option>
//     {TIME_OPTIONS.map((t) => (
//       <option key={t.val} value={t.val}>{t.label}</option>
//     ))}
//   </select>
// );

// const StepIndicator = ({ step }) => (
//   <div className="step-indicator">
//     {[1, 2].map((s, i) => (
//       <React.Fragment key={s}>
//         <div className={`step-dot ${step === s ? "active" : step > s ? "done" : ""}`}>
//           {step > s ? "✓" : s}
//         </div>
//         {i < 1 && <div className={`step-line ${step > s ? "done" : ""}`} />}
//       </React.Fragment>
//     ))}
//   </div>
// );

// const PopupSellar = ({ Cross, id, fetchData, editData }) => {
//   const [step, setStep] = useState(1);
//   const [formData, setFormData] = useState(() => getEmptyFormData(id));
//   const [uploading, setUploading] = useState(false);
//   const [showCopyModal, setShowCopyModal] = useState(false);
//   const [copySourceDay, setCopySourceDay] = useState(null);
//   const [selectedDaysForCopy, setSelectedDaysForCopy] = useState(new Set(DAYS));
//   const token = localStorage.getItem("accessToken");
//   const autocompleteRef = useRef(null);
//   const lastSelectedSuggestionText = useRef("");

//   useEffect(() => {
//     const captureSuggestionText = (e) => {
//       const pacItem = e.target.closest(".pac-item");
//       if (pacItem) {
//         lastSelectedSuggestionText.current = pacItem.textContent
//           .replace(/\s+/g, " ")
//           .trim();
//       }
//     };
//     document.addEventListener("mousedown", captureSuggestionText, true);
//     document.addEventListener("touchstart", captureSuggestionText, true);
//     return () => {
//       document.removeEventListener("mousedown", captureSuggestionText, true);
//       document.removeEventListener("touchstart", captureSuggestionText, true);
//     };
//   }, []);

//   const normalizeTimeValue = (value) => {
//     if (!value && value !== 0) return "";
//     const raw = String(value).trim();
//     if (!raw) return "";
//     const timeMatch = raw.match(/^(\d{1,2}):(\d{2})\s*([aApP][mM])?$/);
//     if (timeMatch) {
//       let hours = Number(timeMatch[1]);
//       const minutes = Number(timeMatch[2]);
//       const meridiem = timeMatch[3]?.toLowerCase();

//       if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) return "";

//       if (meridiem) {
//         if (hours < 1 || hours > 12) return "";
//         if (meridiem === "pm" && hours !== 12) hours += 12;
//         if (meridiem === "am" && hours === 12) hours = 0;
//       } else if (hours < 0 || hours > 23) {
//         return "";
//       }

//       return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
//     }

//     const parsed = dayjs(raw);
//     return parsed.isValid() ? parsed.format("HH:mm") : "";
//   };

//   const getIncomingDayTiming = (timing, day) => {
//     if (!timing || typeof timing !== "object") return { open: false, from: "", to: "" };

//     const candidates = [
//       day,
//       day.toLowerCase(),
//       day.charAt(0).toUpperCase() + day.slice(1),
//       day.slice(0, 3),
//       day.slice(0, 3).toUpperCase(),
//     ];

//     const source = candidates
//       .map((key) => timing[key])
//       .find((entry) => entry && typeof entry === "object");

//     return source || { open: false, from: "", to: "" };
//   };

//   const handleTimeChange = (day, type, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       timing: {
//         ...prev.timing,
//         [day]: { ...prev.timing[day], [type]: value, open: true },
//       },
//     }));
//   };

//   // NEW: toggle 24 Hours for a given day
//   const handleToggle24Hours = (day) => {
//     setFormData((prev) => {
//       const current = prev.timing[day];
//       const is24 = current.from === FULL_DAY_FROM && current.to === FULL_DAY_TO;
//       return {
//         ...prev,
//         timing: {
//           ...prev.timing,
//           [day]: {
//             open: true,
//             from: is24 ? "" : FULL_DAY_FROM,
//             to: is24 ? "" : FULL_DAY_TO,
//           },
//         },
//       };
//     });
//   };

//   useEffect(() => {
//     if (editData) {
//       const locationData = getEditLocationData(editData);
//       const updatedTiming = {};
//       DAYS.forEach((day) => {
//         const source = getIncomingDayTiming(editData?.timing, day);
//         const normalizedFrom = normalizeTimeValue(source.from);
//         const normalizedTo = normalizeTimeValue(source.to);
//         updatedTiming[day] = {
//           open: Boolean(source.open || normalizedFrom || normalizedTo),
//           from: normalizedFrom,
//           to: normalizedTo,
//         };
//       });
//       setStep(1);
//       setFormData({
//         ...getEmptyFormData(editData.Owner || id),
//         Owner: editData.Owner,
//         city: editData.city || "",
//         isActive: editData.isActive,
//         radius: FIXED_RADIUS,
//         markerPosition: {
//           lat: locationData.lat || 25.354826,
//           lng: locationData.long || 55.381007,
//         },
//         location: {
//           country_city: "UAE , Dubai",
//           display_name: locationData.displayName,
//           text: locationData.text,
//           lat: locationData.lat || 24.85,
//           long: locationData.long || 67.05,
//         },
//         estimatedServiceTime: editData.estimatedServiceTime,
//         timing: updatedTiming,
//       });
//       return;
//     }
//     setStep(1);
//     setFormData(getEmptyFormData(id));
//   }, [editData]);

//   const handleActiveChange = (day) => {
//     setFormData((prev) => ({
//       ...prev,
//       timing: {
//         ...prev.timing,
//         [day]: {
//           ...prev.timing[day],
//           open: !prev.timing[day].open,
//           from: prev.timing[day].open ? "" : prev.timing[day].from,
//           to: prev.timing[day].open ? "" : prev.timing[day].to,
//         },
//       },
//     }));
//   };
//   const openCopyModal = (day) => {
//     setCopySourceDay(day);
//     const otherActiveDays = new Set(
//       DAYS.filter((d) => d !== day && formData.timing[d].open)
//     );
//     setSelectedDaysForCopy(otherActiveDays);
//     setShowCopyModal(true);
//   };

//   const handleDayToggleInCopyModal = (day) => {
//     const newSelected = new Set(selectedDaysForCopy);
//     if (newSelected.has(day)) newSelected.delete(day);
//     else newSelected.add(day);
//     setSelectedDaysForCopy(newSelected);
//   };

//   const handleSelectAllDaysForCopy = (selectAll) => {
//     if (selectAll) {
//       setSelectedDaysForCopy(new Set(DAYS.filter((d) => d !== copySourceDay)));
//     } else {
//       setSelectedDaysForCopy(new Set());
//     }
//   };

//   const handleApplyCopyTiming = () => {
//     if (!copySourceDay) return;
//     const { from, to } = formData.timing[copySourceDay];
//     const updatedTiming = { ...formData.timing };
//     selectedDaysForCopy.forEach((day) => {
//       updatedTiming[day] = { open: true, from, to };
//     });
//     setFormData((prev) => ({ ...prev, timing: updatedTiming }));
//     setShowCopyModal(false);
//     toast.success("Times copied to selected days!");
//   };

//   const stripCityCountrySuffix = (text, city, country) => {
//     if (!text) return "";
//     const segments = text.split(" - ").map((s) => s.trim()).filter(Boolean);
//     if (segments.length <= 1) return text.trim();

//     const cityLower = (city || "").toLowerCase();
//     const countryLower = (country || "").toLowerCase();

//     const filtered = segments.filter((seg) => {
//       const segLower = seg.toLowerCase();
//       if (cityLower && segLower === cityLower) return false;
//       if (countryLower && segLower === countryLower) return false;
//       return true;
//     });

//     return (filtered.length > 0 ? filtered : segments).join(" - ").trim();
//   };

//   const getShortDisplayName = (place, city, country, fullText) => {
//     const source = fullText || place.name || place.formatted_address || "";
//     return stripCityCountrySuffix(source, city, country) || place.name || "";
//   };

//   const handlePlaceSelected = (place) => {
//     if (place && place.geometry) {
//       const addressComponents = place.address_components;
//       const city = addressComponents.find(
//         (c) => c.types.includes("locality") || c.types.includes("administrative_area_level_1")
//       )?.long_name || "";
//       const country = addressComponents.find((c) => c.types.includes("country"))?.long_name || "";

//       const selectedText =
//         lastSelectedSuggestionText.current ||
//         place.name ||
//         place.formatted_address ||
//         "";

//       const shortDisplayName = getShortDisplayName(place, city, country, selectedText);

//       setFormData((prev) => ({
//         ...prev,
//         city,
//         location: {
//           country_city: `${country}, ${city}`,
//           display_name: shortDisplayName,
//           text: selectedText,
//           full_address: place.formatted_address || "",
//           lat: place.geometry.location.lat(),
//           long: place.geometry.location.lng(),
//         },
//         markerPosition: {
//           lat: place.geometry.location.lat(),
//           lng: place.geometry.location.lng(),
//         },
//         radius: FIXED_RADIUS,
//       }));

//       if (autocompleteRef.current) {
//         autocompleteRef.current.value = selectedText;
//       }

//       lastSelectedSuggestionText.current = "";
//     }
//   };

//   const generateParkingPositions = (center, radiusInKm) => {
//     const radiusInMeters = radiusInKm * 1000;
//     const positions = [];
//     const numMarkers = Math.floor(Math.random() * 20) + 5;
//     for (let i = 0; i < numMarkers; i++) {
//       const angle = Math.random() * 2 * Math.PI;
//       const distance = Math.random() * radiusInMeters;
//       const offsetLat = (distance * Math.sin(angle)) / 111300;
//       const offsetLng = (distance * Math.cos(angle)) / (111300 * Math.cos((center.lat * Math.PI) / 180));
//       positions.push({ lat: center.lat + offsetLat, lng: center.lng + offsetLng });
//     }
//     return positions;
//   };

//   const handleRadiusChange = (e) => {
//     return e;
//   };

//   const handleMapClick = (e) => {
//     const lat = e.latLng.lat();
//     const lng = e.latLng.lng();
//     setFormData((prev) => ({
//       ...prev,
//       markerPosition: { lat, lng },
//       location: { ...prev.location, lat, long: lng },
//     }));
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const preventWheel = (e) => e.preventDefault();
//   const handleFocus = (e) => e.target.addEventListener("wheel", preventWheel, { passive: false });

//   const validateStep1 = () => {
//     const isAnyActive = DAYS.some((day) => formData.timing[day].open);
//     if (!isAnyActive) { toast.error("Please activate at least one day"); return false; }
//     for (const day of DAYS) {
//       const { from, to, open } = formData.timing[day];
//       if (open && (!from || !to)) {
//         toast.error(`Please set start and end time for ${DAY_LABELS[day]}`);
//         return false;
//       }
//     }
//     return true;
//   };

//   const validateStep2 = () => {
//     if (!formData.location?.text) {
//       toast.error("Please fill all location fields");
//       return false;
//     }
//     if (!formData.location?.display_name?.trim()) {
//       toast.error("Please enter a location display name");
//       return false;
//     }
//     if (!formData.estimatedServiceTime) {
//       toast.error("Please enter estimated service time");
//       return false;
//     }
//     return true;
//   };

//   const nextStep = () => {
//     if (step === 1 && !validateStep1()) return;
//     if (step === 2 && !validateStep2()) return;
//     setStep((s) => s + 1);
//   };

//   const prevStep = () => setStep((s) => s - 1);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateStep1()) return;
//     if (!validateStep2()) return;
//     setUploading(true);
//     try {
//       const payload = {
//         isActive: true,
//         location: {
//           text: formData.location.text,
//           display_name: formData.location.display_name,
//           _id: formData.location._id,
//           lat: formData.location.lat,
//           long: formData.location.long,
//           coordinates: [formData.location.long, formData.location.lat],
//         },
//         timing: Object.keys(formData.timing).reduce((acc, day) => {
//           acc[day] = { open: formData.timing[day].open, from: formData.timing[day].from, to: formData.timing[day].to };
//           return acc;
//         }, {}),
//         radius: FIXED_RADIUS,
//         estimatedServiceTime: formData.estimatedServiceTime,
//       };
//       const apiEndpoint = editData ? `/api/seller/shop/${editData._id}` : "/api/seller/shop";
//       const httpMethod = editData ? "PATCH" : "POST";
//       const response = await axios({
//         method: httpMethod,
//         url: `${API_BASE_URL}${apiEndpoint}`,
//         data: payload,
//         headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
//       });
//       if (response.status >= 200 && response.status < 300) {
//         toast.success(editData ? "Location updated successfully" : "New location saved");
//         fetchData();
//         Cross();
//       } else {
//         toast.error("Failed to submit location details");
//       }
//     } catch (error) {
//       if (error.response?.status === 409) {
//         toast.error("This location already exists");
//       } else if (error.response?.status === 429) {
//         toast.error("Too many requests, please try again later.");
//       } else {
//         toast.error(error?.response?.data?.error || error?.response?.data?.message || "Error submitting location details");
//       }
//     } finally {
//       setUploading(false);
//     }
//   };

//   return (
//     <div className="app">
//       <div className="popup-background">
//         <div className="popup">

//           <div className="popup-header">
//             <h2>
//               {step === 1 ? "Working Hours" : "Location & Radius"}
//             </h2>
//             <ImCross onClick={Cross} className="close-icon" />
//           </div>

//           <StepIndicator step={step} />

//           {step === 1 && (
//             <>
//               <div className="timing-section-header">
//                 <div className="timing-section-title">Set your working hours</div>
//               </div>

//               <div className="timing-list">
//                 {DAYS.map((day) => {
//                   const isActive = formData.timing[day].open;
//                   const is24Hours =
//                     formData.timing[day].from === FULL_DAY_FROM &&
//                     formData.timing[day].to === FULL_DAY_TO;
//                   return (
//                     <div key={day} className={`timing-row-new ${isActive ? "active-day" : ""}`}>
//                       <Switch
//                         onChange={() => handleActiveChange(day)}
//                         checked={isActive}
//                         onColor="#6470dd"
//                         offColor="#ddd"
//                         handleDiameter={18}
//                         uncheckedIcon={false}
//                         checkedIcon={false}
//                         height={22}
//                         width={42}
//                         boxShadow="0px 1px 4px rgba(0,0,0,0.15)"
//                         activeBoxShadow="0px 0px 0px 3px rgba(26,26,46,0.15)"
//                       />

//                       <span className={`day-label-new ${isActive ? "" : "muted"}`}>
//                         {DAY_LABELS[day]}
//                       </span>

//                       {isActive ? (
//                         <>
//                           <label
//                             className="timing-24h-label"
//                             style={{
//                               display: "flex",
//                               alignItems: "center",
//                               gap: "4px",
//                               fontSize: "12px",
//                               marginRight: "8px",
//                               cursor: "pointer",
//                               whiteSpace: "nowrap",
//                             }}
//                           >
//                             <input
//                              style={{border:'1px solid Black'}}
//                               type="checkbox"
//                               checked={is24Hours}
//                               onChange={() => handleToggle24Hours(day)}
//                             />
//                             24 Hrs
//                           </label>

//                           <div className="time-selects-wrap justify-center">
//                             <TimeSelect
//                               value={formData.timing[day].from}
//                               onChange={(val) => handleTimeChange(day, "from", val)}
//                               disabled={is24Hours}
//                             />
//                             <span className="time-dash">–</span>
//                             <TimeSelect
//                               value={formData.timing[day].to}
//                               onChange={(val) => handleTimeChange(day, "to", val)}
//                               disabled={is24Hours}
//                             />
//                           </div>

//                           <div className="timing-actions">
//                             <button
//                               className="timing-icon-btn"
//                               title="Copy to other days"
//                               onClick={() => openCopyModal(day)}
//                             >
//                               <IoCopyOutline size={14} />
//                             </button>
//                           </div>
//                         </>
//                       ) : (
//                         <div className="time-selects-placeholder" />
//                       )}
//                     </div>
//                   );
//                 })}
//               </div>

//               <div className="row-popup">
//                 <button className="btn-left" onClick={Cross}>Cancel</button>
//                 <button className="btn-right" onClick={nextStep}>Continue →</button>
//               </div>
//             </>
//           )}

//           {step === 2 && (
//             <>
//               <div className="addtion-spantag">
//                 <label className="label-popup-seller">Estimated Service Time <sup>*</sup></label>
//                 <input
//                   type="number"
//                   name="estimatedServiceTime"
//                   placeholder="e.g. 45"
//                   value={formData.estimatedServiceTime}
//                   onChange={handleChange}
//                   onFocus={handleFocus}
//                 />
//                 <span className="span-tag-popup" style={{top:'50%'}}>Minutes</span>
//               </div>

//               <label className="label-popup-seller">Location <sup>*</sup></label>
//               <LoadScript googleMapsApiKey="AIzaSyDkYZEMg8ZH5EhRqdkLF-GyeHrhpZBlSd0" libraries={["places"]}>
//                 <Autocomplete
//                   ref={autocompleteRef}
//                   onPlaceSelected={handlePlaceSelected}
//                   options={{ types: ["establishment", "geocode"], componentRestrictions: { country: "AE" } }}
//                   value={formData.location.text}
//                   onChange={(e) =>
//                     setFormData((prev) => ({
//                       ...prev,
//                       location: { ...prev.location, text: e.target.value },
//                     }))
//                   }
//                   placeholder="Search for an address in UAE"
//                   style={{
//                     width: "100%", height: "44px", padding: "0 14px", boxSizing: "border-box",
//                     borderRadius: "10px", border: "1.5px solid #e8e8f0", marginBottom: "14px",
//                     fontSize: "14px", color: "#1a1a2e", background: "#fafafa", outline: "none",
//                   }}
//                 />
//               </LoadScript>

//               <div className="addtion-spantag" style={{ marginTop: 12 }}>
//                 <label className="label-popup-seller">Location display name <sup>*</sup></label>
//                 <input
//                   type="text"
//                   placeholder="e.g. Al Nahda Branch"
//                   value={formData.location.display_name}
//                   onChange={(e) =>
//                     setFormData((prev) => ({
//                       ...prev,
//                       location: { ...prev.location, display_name: e.target.value },
//                     }))
//                   }
//                 />
//               </div>

//               <label className="label-popup-seller">Service Radius <sup>*</sup></label>
//               <div className="addtion-spantag">
//                 <input
//                   type="number"
//                   name="radius"
//                   value={FIXED_RADIUS}
//                   readOnly
//                   disabled
//                   onChange={handleRadiusChange}
//                   style={{ paddingRight: "80px" }}
//                 />
//                 <span className="span-tag-popup">Kilometers</span>
//               </div>

//               <div style={{ marginTop: "14px" }}>
//                 <LoadScript googleMapsApiKey="AIzaSyDkYZEMg8ZH5EhRqdkLF-GyeHrhpZBlSd0" libraries={["places"]} async defer>
//                   <div className="map-wrapper">
//                     <GoogleMap
//                       mapContainerStyle={{ height: "280px", width: "100%" }}
//                       center={formData.markerPosition}
//                       zoom={12}
//                       onClick={handleMapClick}
//                        options={{
//     gestureHandling: "cooperative", 
//     scrollwheel: false,
//     disableDoubleClickZoom: false,
//   }}
//                     >
//                       <Marker position={formData.markerPosition} />
//                       {formData.radius && (
//                         <Circle
//                           center={formData.markerPosition}
//                           radius={FIXED_RADIUS * 1000}
//                           options={{
//                             fillColor: "rgba(116, 126, 239, 0.15)",
//                             strokeColor: "#6b5fe4",
//                             strokeOpacity: 0.8,
//                             strokeWeight: 2,
//                           }}
//                         />
//                       )}
//                       {formData.parkingPositions.map((position, index) => (
//                         <Marker
//                           key={index}
//                           position={position}
//                           icon={{
//                             url: "data:image/svg+xml;base64," + btoa(`
//                               <svg width="69" height="69" viewBox="0 0 69 69" fill="none" xmlns="http://www.w3.org/2000/svg">
//                                 <rect x="2.5" y="2.5" width="64" height="64" rx="32" fill="#6b5fe4"/>
//                                 <rect x="2.5" y="2.5" width="64" height="64" rx="32" stroke="white" stroke-width="5"/>
//                                 <path d="M23.1716 49V21H35.2916C37.7982 21 39.9582 21.4133 41.7716 22.24C43.5849 23.04 44.9849 24.2 45.9716 25.72C46.9582 27.24 47.4516 29.0533 47.4516 31.16C47.4516 33.24 46.9582 35.04 45.9716 36.56C44.9849 38.08 43.5849 39.2533 41.7716 40.08C39.9582 40.88 37.7982 41.28 35.2916 41.28H26.7716L29.6516 38.36V49H23.1716ZM29.6516 39.08L26.7716 36H34.9316C36.9316 36 38.4249 35.5733 39.4116 34.72C40.3982 33.8667 40.8916 32.68 40.8916 31.16C40.8916 29.6133 40.3982 28.4133 39.4116 27.56C38.4249 26.7067 36.9316 26.28 34.9316 26.28H26.7716L29.6516 23.2V39.08Z" fill="white"/>
//                               </svg>
//                             `),
//                             scaledSize: new window.google.maps.Size(30, 30),
//                             anchor: new window.google.maps.Point(30, 20),
//                           }}
//                         />
//                       ))}
//                     </GoogleMap>
//                   </div>
//                 </LoadScript>
//               </div>

//               <div className="row-popup">
//                 <button className="btn-left" onClick={prevStep}>← Back</button>
//                 <button className="btn-right" onClick={handleSubmit} disabled={uploading}>
//                   {uploading ? "Saving..." : editData ? "Update Location" : "Create Location"}
//                 </button>
//               </div>
//             </>
//           )}
//         </div>
//       </div>

//       {showCopyModal && (
//         <div className="copy-modal-overlay">
//           <div className="copy-modal-box">
//             <div className="copy-modal-header">
//               <h3>COPY TIMES TO</h3>
//               <button type="button" className="copy-modal-close" onClick={() => setShowCopyModal(false)}>✕</button>
//             </div>
//             <div className="copy-modal-content">
//               <label className="copy-modal-item full-width">
//                 <input
//                   type="checkbox"
//                   checked={selectedDaysForCopy.size === DAYS.filter(d => d !== copySourceDay).length}
//                   onChange={(e) => handleSelectAllDaysForCopy(e.target.checked)}
//                 />
//                 <span>Select all</span>
//               </label>
//               {DAYS.filter((d) => d !== copySourceDay).map((day) => (
//                 <label key={day} className="copy-modal-item">
//                   <input
//                     type="checkbox"
//                     checked={selectedDaysForCopy.has(day)}
//                     onChange={() => handleDayToggleInCopyModal(day)}
//                   />
//                   <span>{DAY_LABELS[day]}</span>
//                 </label>
//               ))}
//             </div>
//             <div className="copy-modal-footer">
//               <button type="button" className="copy-modal-cancel" onClick={() => setShowCopyModal(false)}>Cancel</button>
//               <button type="button" className="copy-modal-apply" onClick={handleApplyCopyTiming} style={{background:'#6b5fe4'}}>Apply</button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default PopupSellar;